import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertCircle, Mail, Send, Ban, Shield, ShieldCheck,
  Coins, Users as UsersIcon, Globe, Clock, RefreshCw, PencilLine, Plus, Minus,
  Warehouse, ChevronDown, ChevronUp, ChevronRight, Receipt, ArrowDownCircle,
  ArrowUpCircle, Gem, Sparkles, Gauge, Medal, KeyRound, Wrench,
} from 'lucide-react';
import { ItemsView } from './ItemsView';
import { useToast } from '@/shared/contexts/ToastContext';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useConfirm } from '@/shared/components/ui/ConfirmDialog';
import {
  getAdminAccount,
  editAdminAccount,
  editAccountForceGem,
  editAccountMilhas,
  setFullCollection,
  setFullMerito,
  editAccountCredentials,
  changeAccountPassword,
  accountMaintenance,
  panelMail,
  panelSendItem,
  getAccountCashLog,
  getGuardByAccount,
  guardUnban,
  type AccountPanelView,
  type CharacterRow,
  type CashLogRow,
  type HwidDetail,
} from '@/shared/services/adminService';
import { GuardDetailContent, GuardBanModal } from './GuardModals';
import { CLASSES_PT, NATIONS_PT, formatPlaytime } from './labels';

export default function AccountDetailPage() {
  const { userNum } = useParams<{ userNum: string }>();
  const navigate = useNavigate();
  const { notify } = useToast();
  const confirm = useConfirm();

  const [data, setData] = useState<AccountPanelView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Guard card
  const { hasFeature } = useAuth();
  const canBan = hasFeature('ban');
  const [guardData, setGuardData] = useState<HwidDetail | null>(null);
  const [guardLoading, setGuardLoading] = useState(false);
  const [banTarget, setBanTarget] = useState<{ userNum: number; accountId?: string | null } | null>(null);

  const [mailChar, setMailChar] = useState<number | null>(null);
  const [mailMsg, setMailMsg] = useState('');
  const [busyMail, setBusyMail] = useState(false);

  const [itemId, setItemId] = useState('');
  const [itemQty, setItemQty] = useState(1);
  const [itemOption, setItemOption] = useState(0);
  const [itemDuration, setItemDuration] = useState(0);
  const [busyItem, setBusyItem] = useState(false);

  const [editCashDelta, setEditCashDelta] = useState('');
  const [busyEdit, setBusyEdit] = useState(false);

  const [forceDelta, setForceDelta] = useState('');
  const [busyForce, setBusyForce] = useState(false);
  const [busyColl, setBusyColl] = useState(false);
  const [milhasVal, setMilhasVal] = useState('');
  const [busyMilhas, setBusyMilhas] = useState(false);
  const [busyMerito, setBusyMerito] = useState(false);

  const [cred, setCred] = useState({ login: '', email: '', name: '' });
  const [busyCred, setBusyCred] = useState(false);
  const [newPass, setNewPass] = useState('');
  const [busyPass, setBusyPass] = useState(false);
  const [busyManut, setBusyManut] = useState(false);

  const fetchData = useCallback(async () => {
    if (!userNum) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getAdminAccount(parseInt(userNum, 10));
      setData(res);
      if (res.chars?.[0]) setMailChar(res.chars[0].characteridx);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro ao carregar conta');
    } finally {
      setLoading(false);
    }
  }, [userNum]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchGuard = useCallback(async () => {
    if (!userNum) return;
    setGuardLoading(true);
    try {
      const d = await getGuardByAccount(parseInt(userNum, 10));
      setGuardData(d);
    } catch {
      setGuardData(null);
    } finally {
      setGuardLoading(false);
    }
  }, [userNum]);

  useEffect(() => {
    if (data?.acc?.usernum) fetchGuard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.acc?.usernum]);

  const handleGuardUnban = async (un: number) => {
    try {
      await guardUnban(un);
      notify('success', 'Desbanido (ban duplo removido)');
      await Promise.all([fetchData(), fetchGuard()]);
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  const handleMail = async () => {
    if (!data || busyMail || mailChar == null || !mailMsg.trim()) return;
    setBusyMail(true);
    try {
      const res = await panelMail({ char: mailChar, msg: mailMsg });
      notify('success', res.message || 'Mail enviado');
      setMailMsg('');
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusyMail(false);
    }
  };

  const handleEditCash = async (sign: 1 | -1) => {
    if (!data || busyEdit) return;
    const cashDelta = parseInt(editCashDelta, 10) || 0;
    if (!cashDelta) {
      notify('error', 'Informe um valor de Cash');
      return;
    }
    setBusyEdit(true);
    try {
      const res = await editAdminAccount(data.acc.usernum, {
        addCash: cashDelta * sign,
      });
      notify('success', res.message || 'Cash atualizado');
      setEditCashDelta('');
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusyEdit(false);
    }
  };

  const handleSendItem = async () => {
    if (!data || busyItem) return;
    const id = parseInt(itemId, 10);
    if (!id) { notify('error', 'ItemID inválido'); return; }
    setBusyItem(true);
    try {
      const res = await panelSendItem({
        userNum: data.acc.usernum,
        itemId: id,
        itemOption,
        itemDuration,
        itemQty,
      });
      notify('success', res.message || 'Item enviado');
      setItemId('');
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusyItem(false);
    }
  };

  const handleForceGem = async (sign: 1 | -1) => {
    if (!data || busyForce) return;
    const amount = (parseInt(forceDelta, 10) || 0) * sign;
    if (!amount) { notify('error', 'Informe um valor de ForceGem'); return; }
    const ok = await confirm({
      title: sign > 0 ? 'Adicionar ForceGem' : 'Remover ForceGem',
      description: `${sign > 0 ? '+' : ''}${amount} ForceGem na conta ${data.acc.id || data.acc.usernum}?`,
      tone: 'warning', confirmLabel: 'Confirmar',
    });
    if (!ok) return;
    setBusyForce(true);
    try {
      const res = await editAccountForceGem(data.acc.usernum, { amount });
      notify('success', res.message || 'ForceGem atualizado');
      setForceDelta('');
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusyForce(false);
    }
  };

  const handleFullCollection = async () => {
    if (!data || busyColl) return;
    const ok = await confirm({
      title: 'Aplicar Coleção Completa',
      description: `Aplicar a coleção completa na conta ${data.acc.id || data.acc.usernum}? Sobrescreve a coleção atual do jogador.`,
      tone: 'danger', confirmLabel: 'Aplicar',
    });
    if (!ok) return;
    setBusyColl(true);
    try {
      const res = await setFullCollection(data.acc.usernum);
      notify('success', res.message || 'Coleção completa aplicada');
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusyColl(false);
    }
  };

  const handleMilhas = async () => {
    if (!data || busyMilhas) return;
    const amount = parseInt(milhasVal, 10);
    if (Number.isNaN(amount) || amount < 0) { notify('error', 'Informe um valor de Milhas (>= 0)'); return; }
    const ok = await confirm({
      title: 'Editar Milhas',
      description: `Ajustar Milhas para ${amount.toLocaleString('pt-BR')} na conta ${data.acc.id || data.acc.usernum}?`,
      tone: 'warning', confirmLabel: 'Aplicar',
    });
    if (!ok) return;
    setBusyMilhas(true);
    try {
      const res = await editAccountMilhas(data.acc.usernum, { amount });
      notify('success', res.message || 'Milhas atualizadas');
      setMilhasVal('');
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusyMilhas(false);
    }
  };

  const handleMerito = async () => {
    if (!data || busyMerito) return;
    const ok = await confirm({
      title: 'Aplicar Mérito Completo',
      description: `Aplicar mérito completo na conta ${data.acc.id || data.acc.usernum}?`,
      tone: 'warning', confirmLabel: 'Aplicar',
    });
    if (!ok) return;
    setBusyMerito(true);
    try {
      const res = await setFullMerito(data.acc.usernum);
      notify('success', res.message || 'Mérito completo aplicado');
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusyMerito(false);
    }
  };

  const handleCredentials = async () => {
    if (!data || busyCred) return;
    const payload: { login?: string; email?: string; name?: string } = {};
    if (cred.login.trim()) payload.login = cred.login.trim();
    if (cred.email.trim()) payload.email = cred.email.trim();
    if (cred.name.trim()) payload.name = cred.name.trim();
    if (Object.keys(payload).length === 0) { notify('error', 'Preencha ao menos um campo'); return; }
    const ok = await confirm({
      title: 'Editar credenciais',
      description: `Alterar ${Object.keys(payload).join(', ')} da conta ${data.acc.id || data.acc.usernum}?`,
      tone: 'warning', confirmLabel: 'Salvar',
    });
    if (!ok) return;
    setBusyCred(true);
    try {
      const res = await editAccountCredentials(data.acc.usernum, payload);
      notify('success', res.message || 'Credenciais atualizadas');
      setCred({ login: '', email: '', name: '' });
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusyCred(false);
    }
  };

  const handleChangePassword = async () => {
    if (!data || busyPass) return;
    const pw = newPass.trim();
    if (pw.length < 4) { notify('error', 'Senha deve ter ao menos 4 caracteres'); return; }
    const ok = await confirm({
      title: 'Alterar Senha',
      description: `Definir uma nova senha para a conta ${data.acc.id || data.acc.usernum}? O jogador passará a usar a senha nova.`,
      tone: 'warning',
      confirmLabel: 'Alterar senha',
    });
    if (!ok) return;
    setBusyPass(true);
    try {
      const res = await changeAccountPassword(data.acc.usernum, pw);
      notify('success', res.message || 'Senha alterada');
      setNewPass('');
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusyPass(false);
    }
  };

  const handleManutencao = async (action: 'enable' | 'disable') => {
    if (!data || busyManut) return;
    const ok = await confirm({
      title: action === 'enable' ? 'Entrar em manutenção' : 'Sair de manutenção',
      description: action === 'enable'
        ? `Trocar a senha da conta ${data.acc.id || data.acc.usernum} para a senha master (@Liberty@ / sub 1234)? A senha original é guardada e restaurada ao sair.`
        : `Restaurar as senhas originais da conta ${data.acc.id || data.acc.usernum}?`,
      tone: action === 'enable' ? 'danger' : 'warning',
      confirmLabel: action === 'enable' ? 'Entrar em manutenção' : 'Restaurar',
    });
    if (!ok) return;
    setBusyManut(true);
    try {
      const res = await accountMaintenance(data.acc.usernum, action);
      notify('success', res.message || 'OK');
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusyManut(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20 text-white/40">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="surface-card rounded-xl p-6">
        <div className="flex items-start gap-3">
          <AlertCircle size={20} className="text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-red-400">Falha</p>
            <p className="text-xs text-white/65 mt-1">{error}</p>
          </div>
          <button
            onClick={() => navigate('/admin/accounts')}
            className="px-3 py-1.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.08]
                       text-xs text-white/75 transition cursor-pointer"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  const { acc, chars, cash, warehouse, banneds } = data;
  const isBanned = acc.authtype === 2 || banneds.length > 0;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <Link
            to="/admin/accounts"
            className="inline-flex items-center gap-1.5 text-xs text-white/55
                       hover:text-[#00e1d7] mb-2 transition"
          >
            <ArrowLeft size={14} />
            Voltar para Contas
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            {acc.id || `UserNum ${acc.usernum}`}
            {isBanned ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold
                               bg-red-500/15 text-red-400 border border-red-500/25">
                <Ban size={11} /> Banido
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold
                               bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                <ShieldCheck size={11} /> Ativo
              </span>
            )}
          </h1>
          <p className="text-sm text-white/65 mt-1">
            UserNum {acc.usernum} · {acc.email || 'sem email'}
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                     bg-[#00e1d7]/10 border border-[#00e1d7]/25 text-[#00e1d7]
                     hover:bg-[#00e1d7]/20 transition cursor-pointer disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Atualizar
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Cash" value={(cash?.cash ?? 0).toLocaleString('pt-BR')} icon={<Coins size={16} />} />
        <Stat label="Alz Armazém" value={(warehouse?.alz ?? 0).toLocaleString('pt-BR')} icon={<Coins size={16} />} />
        <Stat label="Personagens" value={String(chars.length)} icon={<UsersIcon size={16} />} />
      </div>

      <Section title="Conta">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <Field label="Nome" value={acc.username || '—'} />
          <Field label="Último IP" value={acc.lastip || '—'} mono />
          <Field label="AuthType" value={String(acc.authtype ?? '—')} mono />
          <Field label="Email" value={acc.email || '—'} />
        </div>
      </Section>

      <Section title={`Personagens (${chars.length})`} icon={<UsersIcon size={16} />}>
        {chars.length === 0 ? (
          <p className="text-sm text-white/40 py-4">Nenhum personagem.</p>
        ) : (
          <div className="space-y-2">
            {chars.map((c) => <CharCard key={c.characteridx} c={c} />)}
          </div>
        )}
      </Section>

      {/* Armazém da CONTA (UserNum) */}
      <WarehouseSection userNum={acc.usernum} />

      {/* Histórico de transações Cash (CashLog) */}
      <CashLogSection userNum={acc.usernum} />

      {/* Card Guard — linha cheia. HWID, contas, histórico, ban/unban duplo. */}
      <div className="surface-card rounded-xl p-5 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider flex items-center gap-2">
            <Shield size={14} className={isBanned ? 'text-red-400' : 'text-[#00e1d7]'} />
            Guard {isBanned && <span className="text-[10px] px-2 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/30">CONTA BANIDA</span>}
          </h3>
          <div className="flex items-center gap-2">
            {canBan && (
              <button
                onClick={() => setBanTarget({ userNum: acc.usernum, accountId: acc.username })}
                className="px-3 py-1.5 rounded text-xs font-semibold
                           bg-red-500/15 border border-red-500/30 text-red-400
                           hover:bg-red-500/25 transition cursor-pointer flex items-center gap-1.5"
              >
                <Ban size={12} /> Banir conta + HWID
              </button>
            )}
            <button
              onClick={fetchGuard}
              disabled={guardLoading}
              className="p-1.5 rounded hover:bg-white/[0.05] text-white/55 cursor-pointer disabled:opacity-50"
              title="Atualizar"
            >
              <RefreshCw size={14} className={guardLoading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
        {guardLoading ? (
          <div className="py-8 flex items-center justify-center text-white/40">
            <Loader2 size={18} className="animate-spin" />
          </div>
        ) : guardData ? (
          <GuardDetailContent
            data={guardData}
            canBan={canBan}
            onBan={(un, accId) => setBanTarget({ userNum: un, accountId: accId })}
            onUnban={handleGuardUnban}
          />
        ) : (
          <div className="text-sm text-white/40 text-center py-6">Sem dados do guard</div>
        )}
      </div>

      <h2 className="text-base font-bold text-white/90 uppercase tracking-wider mt-6 mb-1 flex items-center gap-2">
        <Sparkles size={16} className="text-[#00e1d7]" /> Ações GM
      </h2>
      <div className="h-px bg-white/[0.08] mb-3" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="surface-card rounded-xl p-5">
          <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Mail size={14} className="text-[#00e1d7]" /> Enviar Mail (in-game)
          </h3>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">Personagem destinatário</label>
              <select
                value={mailChar ?? ''}
                onChange={(e) => setMailChar(parseInt(e.target.value))}
                className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
              >
                {chars.map((c) => (
                  <option key={c.characteridx} value={c.characteridx}>
                    {c.name} (Lv.{c.lev})
                  </option>
                ))}
              </select>
            </div>
            <textarea
              value={mailMsg}
              onChange={(e) => setMailMsg(e.target.value)}
              placeholder="Mensagem"
              rows={3}
              className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white resize-none"
            />
            <button
              onClick={handleMail}
              disabled={busyMail || !mailMsg.trim() || mailChar == null}
              className="w-full px-4 py-2.5 rounded-lg bg-[#00e1d7]/15 border border-[#00e1d7]/30
                         text-[#00e1d7] font-semibold text-sm hover:bg-[#00e1d7]/25 transition
                         cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busyMail ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Enviar
            </button>
          </div>
        </div>

        <div className="surface-card rounded-xl p-5">
          <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider mb-4 flex items-center gap-2">
            <PencilLine size={14} className="text-violet-400" /> Editar Cash
          </h3>
          <div className="mb-4">
            <label className="text-[11px] text-white/55 mb-1 block">Cash (adicionar / remover)</label>
            <input
              type="text" inputMode="numeric" value={editCashDelta}
              onChange={(e) => setEditCashDelta(e.target.value.replace(/\D/g, ''))}
              placeholder="0"
              className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
            />
            <p className="text-[10px] text-white/35 mt-1">
              Atual: <span className="text-white/65">{(cash?.cash ?? 0).toLocaleString('pt-BR')}</span>
              <span className="text-white/25"> · vai direto pra conta (entra no Cash Total)</span>
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleEditCash(1)}
              disabled={busyEdit}
              className="flex-1 px-4 py-2.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30
                         text-emerald-400 font-semibold text-sm hover:bg-emerald-500/25 transition
                         cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busyEdit ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Adicionar
            </button>
            <button
              onClick={() => handleEditCash(-1)}
              disabled={busyEdit}
              className="flex-1 px-4 py-2.5 rounded-lg bg-red-500/15 border border-red-500/30
                         text-red-400 font-semibold text-sm hover:bg-red-500/25 transition
                         cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busyEdit ? <Loader2 size={14} className="animate-spin" /> : <Minus size={14} />}
              Remover
            </button>
          </div>
        </div>

        <div className="surface-card rounded-xl p-5 lg:col-span-2">
          <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Send size={14} className="text-amber-400" /> Enviar Item (cash inventory)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="md:col-span-2">
              <label className="text-[11px] text-white/55 mb-1 block">Item ID</label>
              <input
                type="text" inputMode="numeric" value={itemId}
                onChange={(e) => setItemId(e.target.value.replace(/\D/g, ''))}
                placeholder="ex: 12345"
                className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
              />
            </div>
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">Opção</label>
              <input type="number" value={itemOption}
                onChange={(e) => setItemOption(parseInt(e.target.value) || 0)}
                className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">Qtd</label>
              <input type="number" min={1} value={itemQty}
                onChange={(e) => setItemQty(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">Duração</label>
              <input type="number" value={itemDuration}
                onChange={(e) => setItemDuration(parseInt(e.target.value) || 0)}
                className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white" />
            </div>
          </div>
          <button
            onClick={handleSendItem}
            disabled={busyItem || !itemId}
            className="mt-4 w-full px-4 py-2.5 rounded-lg bg-amber-500/15 border border-amber-500/30
                       text-amber-400 font-semibold text-sm hover:bg-amber-500/25 transition
                       cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busyItem ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Enviar Item
          </button>
        </div>

        {hasFeature('edit_account_forcegem') && (
          <div className="surface-card rounded-xl p-5">
            <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Gem size={14} className="text-fuchsia-400" /> ForceGem
            </h3>
            <label className="text-[11px] text-white/55 mb-1 block">Valor (delta)</label>
            <input
              type="text" inputMode="numeric" value={forceDelta}
              onChange={(e) => setForceDelta(e.target.value.replace(/\D/g, ''))}
              placeholder="0"
              className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white mb-4"
            />
            <div className="flex gap-2">
              <button
                onClick={() => handleForceGem(1)} disabled={busyForce}
                className="flex-1 px-4 py-2.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30
                           text-emerald-400 font-semibold text-sm hover:bg-emerald-500/25 transition
                           cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {busyForce ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Adicionar
              </button>
              <button
                onClick={() => handleForceGem(-1)} disabled={busyForce}
                className="flex-1 px-4 py-2.5 rounded-lg bg-red-500/15 border border-red-500/30
                           text-red-400 font-semibold text-sm hover:bg-red-500/25 transition
                           cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {busyForce ? <Loader2 size={14} className="animate-spin" /> : <Minus size={14} />}
                Remover
              </button>
            </div>
          </div>
        )}

        {hasFeature('edit_account_milhas') && (
          <div className="surface-card rounded-xl p-5">
            <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Gauge size={14} className="text-sky-400" /> Milhas
            </h3>
            <label className="text-[11px] text-white/55 mb-1 block">Valor (set absoluto)</label>
            <input
              type="text" inputMode="numeric" value={milhasVal}
              onChange={(e) => setMilhasVal(e.target.value.replace(/\D/g, ''))}
              placeholder="0"
              className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white mb-4"
            />
            <button
              onClick={handleMilhas} disabled={busyMilhas}
              className="w-full px-4 py-2.5 rounded-lg bg-sky-500/15 border border-sky-500/30
                         text-sky-400 font-semibold text-sm hover:bg-sky-500/25 transition
                         cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busyMilhas ? <Loader2 size={14} className="animate-spin" /> : <Gauge size={14} />}
              Aplicar Milhas
            </button>
          </div>
        )}

        {hasFeature('edit_account_full_collection') && (
          <div className="surface-card rounded-xl p-5">
            <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Sparkles size={14} className="text-amber-400" /> Coleção Completa
            </h3>
            <p className="text-xs text-white/55 mb-4">
              Aplica o blob de coleção completa na conta. Sobrescreve a coleção atual do jogador.
            </p>
            <button
              onClick={handleFullCollection} disabled={busyColl}
              className="w-full px-4 py-2.5 rounded-lg bg-amber-500/15 border border-amber-500/30
                         text-amber-400 font-semibold text-sm hover:bg-amber-500/25 transition
                         cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busyColl ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              Aplicar Coleção Completa
            </button>
          </div>
        )}

        {hasFeature('edit_account_full_merito') && (
          <div className="surface-card rounded-xl p-5">
            <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Medal size={14} className="text-emerald-400" /> Mérito Completo
            </h3>
            <p className="text-xs text-white/55 mb-4">
              Aplica MeritPoint + mastery de mérito completos na conta.
            </p>
            <button
              onClick={handleMerito} disabled={busyMerito}
              className="w-full px-4 py-2.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30
                         text-emerald-400 font-semibold text-sm hover:bg-emerald-500/25 transition
                         cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busyMerito ? <Loader2 size={14} className="animate-spin" /> : <Medal size={14} />}
              Aplicar Mérito
            </button>
          </div>
        )}

        {hasFeature('edit_account_credentials') && (
          <div className="surface-card rounded-xl p-5">
            <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider mb-4 flex items-center gap-2">
              <KeyRound size={14} className="text-violet-400" /> Credenciais
            </h3>
            <p className="text-[10px] text-white/40 mb-3">Deixe em branco o que não quiser mudar.</p>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] text-white/55 mb-1 block">Novo login (3-20)</label>
                <input
                  type="text" value={cred.login}
                  onChange={(e) => setCred((c) => ({ ...c, login: e.target.value }))}
                  placeholder={acc.id || 'login'} autoComplete="off"
                  className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
                />
              </div>
              <div>
                <label className="text-[11px] text-white/55 mb-1 block">Email / Telefone</label>
                <input
                  type="text" value={cred.email}
                  onChange={(e) => setCred((c) => ({ ...c, email: e.target.value }))}
                  placeholder={acc.email || 'email ou telefone'}
                  className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
                />
              </div>
              <div>
                <label className="text-[11px] text-white/55 mb-1 block">Nome do titular</label>
                <input
                  type="text" value={cred.name}
                  onChange={(e) => setCred((c) => ({ ...c, name: e.target.value }))}
                  placeholder={acc.username || 'nome'}
                  className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
                />
              </div>
              <button
                onClick={handleCredentials} disabled={busyCred}
                className="w-full px-4 py-2.5 rounded-lg bg-violet-500/15 border border-violet-500/30
                           text-violet-400 font-semibold text-sm hover:bg-violet-500/25 transition
                           cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {busyCred ? <Loader2 size={14} className="animate-spin" /> : <PencilLine size={14} />}
                Salvar credenciais
              </button>
            </div>
          </div>
        )}

        {hasFeature('account_password') && (
          <div className="surface-card rounded-xl p-5">
            <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider mb-4 flex items-center gap-2">
              <KeyRound size={14} className="text-violet-400" /> Alterar Senha
            </h3>
            <p className="text-[10px] text-white/40 mb-3">Define uma nova senha de login para a conta.</p>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] text-white/55 mb-1 block">Nova senha (mín. 4)</label>
                <input
                  type="password" value={newPass}
                  onChange={(e) => setNewPass(e.target.value)}
                  placeholder="nova senha" autoComplete="new-password"
                  className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white"
                />
              </div>
              <button
                onClick={handleChangePassword} disabled={busyPass || newPass.trim().length < 4}
                className="w-full px-4 py-2.5 rounded-lg bg-violet-500/15 border border-violet-500/30
                           text-violet-400 font-semibold text-sm hover:bg-violet-500/25 transition
                           cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {busyPass ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                Alterar Senha
              </button>
            </div>
          </div>
        )}

        {hasFeature('account_maintenance') && (
          <div className="surface-card rounded-xl p-5">
            <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Wrench size={14} className="text-amber-400" /> Manutenção
              {data.inMaintenance && (
                <span className="text-[10px] px-2 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/30 normal-case">
                  em manutenção
                </span>
              )}
            </h3>
            <p className="text-xs text-white/55 mb-4">
              {data.inMaintenance ? (
                <>A conta está <b>em manutenção</b> (senha master <span className="font-mono">@Liberty@</span> / sub{' '}
                <span className="font-mono">1234</span>). Encerrar restaura a senha original do jogador.</>
              ) : (
                <>Ao iniciar, a senha vira a master (<span className="font-mono">@Liberty@</span> / sub{' '}
                <span className="font-mono">1234</span>) e a original fica guardada. Encerrar depois restaura.</>
              )}
            </p>
            <button
              onClick={() => handleManutencao(data.inMaintenance ? 'disable' : 'enable')} disabled={busyManut}
              className={`w-full px-4 py-2.5 rounded-lg border font-semibold text-sm transition
                         cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 ${
                           data.inMaintenance
                             ? 'bg-red-500/15 border-red-500/30 text-red-400 hover:bg-red-500/25'
                             : 'bg-amber-500/15 border-amber-500/30 text-amber-400 hover:bg-amber-500/25'
                         }`}
            >
              {busyManut
                ? <Loader2 size={14} className="animate-spin" />
                : (data.inMaintenance ? <ShieldCheck size={14} /> : <Wrench size={14} />)}
              {data.inMaintenance ? 'Encerrar Manutenção' : 'Iniciar Manutenção'}
            </button>
          </div>
        )}
      </div>

      {banTarget && (
        <GuardBanModal
          target={banTarget}
          onClose={() => setBanTarget(null)}
          onDone={() => {
            setBanTarget(null);
            Promise.all([fetchData(), fetchGuard()]);
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div className="surface-card rounded-xl p-3.5">
      <div className="flex items-center gap-2 text-[11px] text-white/55 uppercase tracking-wider font-bold mb-1.5">
        <span className="text-[#00e1d7]">{icon}</span>
        {label}
      </div>
      <p className="text-xl font-black text-white leading-none">{value}</p>
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="surface-card rounded-xl p-5 mt-4">
      <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider mb-3 flex items-center gap-2">
        {icon}
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-[11px] text-white/45 uppercase tracking-wider">{label}</p>
      <p className={`text-sm text-white/90 ${mono ? 'font-mono' : ''}`}>{value}</p>
    </div>
  );
}

function CharCard({ c }: { c: CharacterRow }) {
  const online = c.login === 1;

  return (
    <Link
      to={`/admin/character/${c.characteridx}`}
      className="flex items-center justify-between gap-3 bg-[#0d141b] border border-white/[0.04] rounded-lg px-4 py-3
                 hover:border-[#00e1d7]/30 hover:bg-[#0e171f] transition cursor-pointer group"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-white truncate group-hover:text-[#00e1d7] transition">{c.name}</span>
          {online && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#00e1d7]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00e1d7]" />
              online
            </span>
          )}
        </div>
        <div className="text-[11px] text-white/55 mt-0.5 flex flex-wrap items-center gap-2.5">
          <span>Lv.<span className="text-white/85 font-semibold">{c.lev}</span></span>
          {c.ov ? <span className="text-amber-400">OV {c.ov}</span> : null}
          {c.class != null ? <span>{CLASSES_PT[c.class] || `Cl ${c.class}`}</span> : null}
          {c.nation ? (
            <span className="inline-flex items-center gap-1">
              <Globe size={10} />
              {NATIONS_PT[c.nation]?.name || `N${c.nation}`}
            </span>
          ) : null}
          <span>Alz {(c.alz ?? 0).toLocaleString('pt-BR')}</span>
          {c.playtime ? (
            <span className="inline-flex items-center gap-1">
              <Clock size={10} />
              {formatPlaytime(c.playtime)}
            </span>
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[10px] font-mono text-white/35">#{c.characteridx}</span>
        <ChevronRight size={14} className="text-white/30 group-hover:text-[#00e1d7] transition" />
      </div>
    </Link>
  );
}

/* ─────────────────────────────────────── CashLog ─────────────────────── */

const LOGTYPE_LABELS: Record<number, { label: string; cls: string }> = {
  // Site/painel (100+)
  100: { label: 'Recarga (site)', cls: 'text-emerald-400' },
  101: { label: 'Cartão Presente (site)', cls: 'text-amber-400' },
  102: { label: 'Compra Premium (site)', cls: 'text-violet-400' },
  103: { label: 'Ajuste GM', cls: 'text-sky-400' },
  // Comuns do jogo
  1: { label: 'Recarga (in-game)', cls: 'text-emerald-300' },
  2: { label: 'Compra Loja Cash', cls: 'text-orange-300' },
  3: { label: 'Reembolso', cls: 'text-rose-300' },
};

function getLogTypeLabel(t: number): { label: string; cls: string } {
  return LOGTYPE_LABELS[t] || { label: `Tipo ${t}`, cls: 'text-white/65' };
}

function CashLogSection({ userNum }: { userNum: number }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<CashLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const pageSize = 25;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAccountCashLog(userNum, page * pageSize, pageSize);
      setItems(res.items);
      setTotal(res.total);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  }, [userNum, page]);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="surface-card rounded-xl mt-4 overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 p-5 hover:bg-white/[0.02] transition cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <Receipt size={16} className="text-[#00e1d7]" />
          <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider">
            Histórico de Transações Cash
          </h3>
          {open && total > 0 && (
            <span className="text-[11px] text-white/45 normal-case">({total.toLocaleString('pt-BR')})</span>
          )}
        </div>
        {open ? <ChevronUp size={16} className="text-white/55" /> : <ChevronDown size={16} className="text-white/55" />}
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-white/[0.04]">
          {error && (
            <div className="rounded-lg p-3 mb-3 bg-red-500/10 border border-red-500/30 flex items-start gap-2">
              <AlertCircle size={14} className="text-red-400 shrink-0 mt-0.5" />
              <p className="text-xs text-red-400">{error}</p>
            </div>
          )}

          {loading && items.length === 0 ? (
            <div className="py-12 flex items-center justify-center text-white/40">
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-white/40 text-sm">Nenhuma transação</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-white/45 border-b border-white/[0.06]">
                      <th className="text-left py-2 pr-3 font-bold">Data</th>
                      <th className="text-left py-2 pr-3 font-bold">Tipo</th>
                      <th className="text-right py-2 pr-3 font-bold">Pré</th>
                      <th className="text-right py-2 pr-3 font-bold">Δ</th>
                      <th className="text-right py-2 pr-3 font-bold">Pós</th>
                      <th className="text-right py-2 pr-3 font-bold">Item</th>
                      <th className="text-right py-2 font-bold">TranNo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => {
                      const meta = getLogTypeLabel(it.logType);
                      const positive = (it.changeCash ?? 0) > 0;
                      return (
                        <tr key={it.cashLogNo} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                          <td className="py-2 pr-3 font-mono text-[11px] text-white/65">
                            {new Date(it.logDatetime).toLocaleString('pt-BR')}
                          </td>
                          <td className={`py-2 pr-3 font-semibold ${meta.cls}`}>{meta.label}</td>
                          <td className="py-2 pr-3 text-right font-mono text-white/55">
                            {it.cashPre.toLocaleString('pt-BR')}
                          </td>
                          <td className="py-2 pr-3 text-right font-mono font-bold">
                            <span className={`inline-flex items-center gap-0.5 ${positive ? 'text-emerald-400' : 'text-rose-400'}`}>
                              {positive ? <ArrowUpCircle size={11} /> : <ArrowDownCircle size={11} />}
                              {Math.abs(it.changeCash).toLocaleString('pt-BR')}
                            </span>
                          </td>
                          <td className="py-2 pr-3 text-right font-mono text-white/90 font-semibold">
                            {it.cashAfter.toLocaleString('pt-BR')}
                          </td>
                          <td className="py-2 pr-3 text-right font-mono text-white/55">
                            {it.itemCd > 0 ? `#${it.itemCd}${(it.quantity ?? 1) > 1 ? ` ×${it.quantity}` : ''}` : '—'}
                          </td>
                          <td className="py-2 text-right font-mono text-white/45 text-[11px]">
                            {it.tranNo > 0 ? it.tranNo : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-3 text-[11px] text-white/55">
                  <span>Página {page + 1} de {totalPages}</span>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => page > 0 && setPage((p) => p - 1)}
                      disabled={page === 0 || loading}
                      className="px-2.5 py-1 rounded-md bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => page + 1 < totalPages && setPage((p) => p + 1)}
                      disabled={page + 1 >= totalPages || loading}
                      className="px-2.5 py-1 rounded-md bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
                    >
                      Próxima
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function WarehouseSection({ userNum }: { userNum: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="surface-card rounded-xl mt-4 overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 p-5 hover:bg-white/[0.02] transition cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <Warehouse size={16} className="text-[#00e1d7]" />
          <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider">Armazém da Conta</h3>
        </div>
        {open ? <ChevronUp size={16} className="text-white/55" /> : <ChevronDown size={16} className="text-white/55" />}
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-white/[0.04]">
          <ItemsView source="warehouse" id={userNum} />
        </div>
      )}
    </div>
  );
}
